import { LangSwitcher } from '@/components/i18n/LangSwitcher';
import { getTemplate } from '@/components/templates/registry';
import { useT } from '@/lib/i18n';
import { navigate } from '@/lib/nav';
import { useFicheStore } from '@/lib/state/ficheStore';
import { type FicheSummary, deleteFiche, listFiches, loadFiche } from '@/lib/storage/db';
import { useEffect, useState } from 'react';

export function LibraryRoute() {
  const { t, lang } = useT();
  const [summaries, setSummaries] = useState<FicheSummary[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const putFiche = useFicheStore((s) => s.put);

  useEffect(() => {
    listFiches()
      .then(setSummaries)
      .catch((err) => {
        console.warn('Library list failed:', err);
        setSummaries([]);
      });
  }, []);

  const onOpen = async (id: string) => {
    setBusyId(id);
    const f = await loadFiche(id);
    setBusyId(null);
    if (f) {
      putFiche(f);
      navigate(`/editor/${f.id}`);
    }
  };

  const onDelete = async (id: string, title: string) => {
    if (!window.confirm(t('library.delete_confirm', { title }))) return;
    await deleteFiche(id);
    setSummaries((prev) => prev?.filter((s) => s.id !== id) ?? null);
  };

  return (
    <main
      data-testid="library-root"
      className="min-h-screen px-4 py-8"
      style={{
        backgroundImage:
          'radial-gradient(circle at 20% 30%, rgba(255,200,140,.18), transparent 40%), radial-gradient(circle at 80% 70%, rgba(140,180,255,.15), transparent 45%), linear-gradient(#ece5d2, #e0d8c0)',
      }}
    >
      <div className="max-w-5xl mx-auto">
        <header className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <h1 className="font-gloock text-3xl text-ink">{t('library.title')}</h1>
          <div className="flex items-center gap-3">
            <LangSwitcher />
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-accent-yellow text-ink font-gloock text-[15px] border-[2.5px] border-ink rounded-card shadow-ink-sm hover:translate-y-[-1px] transition-transform"
              data-testid="new-fiche-button"
            >
              {t('library.new')}
            </button>
          </div>
        </header>

        {summaries === null ? (
          <p className="font-caveat text-ink-soft text-[18px]">{t('library.loading')}</p>
        ) : summaries.length === 0 ? (
          <div
            className="rounded-card border-[2.5px] border-ink bg-paper p-10 text-center"
            data-testid="library-empty"
          >
            <div className="text-5xl mb-2" aria-hidden>
              📭
            </div>
            <h2 className="font-gloock text-xl text-ink">{t('library.empty.title')}</h2>
            <p className="font-caveat text-ink-soft text-[16px] mt-2">{t('library.empty.body')}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-5 px-4 py-2 bg-accent-blue text-white font-hand text-[15px] border-2 border-ink rounded-control shadow-ink-sm"
            >
              {t('library.empty.cta')}
            </button>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0">
            {summaries.map((s) => {
              const tpl = getTemplate(s.template_id);
              return (
                <li key={s.id}>
                  <article
                    className="bg-paper border-[2.5px] border-ink rounded-card p-4 shadow-ink-sm h-full flex flex-col"
                    data-testid="library-card"
                  >
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="font-hand text-[12px] text-ink-soft">{tpl.label}</span>
                      <span className="font-mono text-[11px] text-ink-soft">
                        {s.id.slice(0, 6)}
                      </span>
                    </div>
                    <h2 className="font-gloock text-[18px] text-ink leading-tight">{s.title}</h2>
                    <p className="font-caveat text-ink-soft text-[15px] mt-1 flex-1">
                      {t(
                        s.section_count === 1 ? 'library.sections_one' : 'library.sections_other',
                        {
                          n: s.section_count,
                        },
                      )}{' '}
                      ·{' '}
                      {t(s.tag_count === 1 ? 'library.tags_one' : 'library.tags_other', {
                        n: s.tag_count,
                      })}
                      <br />
                      {t('library.updated_at')}&nbsp;: {fmtDate(s.updated_at, lang)}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => onOpen(s.id)}
                        disabled={busyId === s.id}
                        className="flex-1 px-3 py-1.5 bg-accent-blue text-white font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm disabled:opacity-50"
                        data-testid="library-open"
                      >
                        {busyId === s.id ? t('library.opening') : t('library.open')}
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(s.id, s.title)}
                        className="px-3 py-1.5 min-h-[40px] bg-white text-warn-text font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm hover:bg-warn-bg"
                        aria-label={t('library.delete_aria', { title: s.title })}
                      >
                        🗑️
                      </button>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}

function fmtDate(iso: string, lang: 'fr' | 'en' = 'fr'): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
